/**
 * French postal-code → department code lookup (Corse, DOM-TOM).
 */
import type { DepartmentRef } from '@/types/geography.types';

export type { DepartmentRef };

/** France postal rules: CP → department code (Corse 2A/2B, DOM-TOM 3 digits). */
export function postalCodeToDepartmentCode(postalCode: string): string | undefined {
  const digits = postalCode.replace(/\s/g, '');
  if (digits.length < 2) return undefined;

  if (
    digits.startsWith('971') ||
    digits.startsWith('972') ||
    digits.startsWith('973') ||
    digits.startsWith('974') ||
    digits.startsWith('976')
  ) {
    return digits.slice(0, 3);
  }

  const prefix = digits.slice(0, 2);
  if (prefix === '20') {
    if (digits >= '20000' && digits <= '20199') return '2A';
    if (digits >= '20200' && digits <= '20620') return '2B';
    return undefined;
  }

  return prefix;
}

/** Catalogue row whose `code` matches; undefined when missing. */
export function findDepartmentByCode(
  departments: DepartmentRef[],
  code: string,
): DepartmentRef | undefined {
  return departments.find((d) => d.code === code);
}

/** Catalogue row from a postal code (Corse / DOM-TOM rules). */
export function findDepartmentByPostalCode(
  departments: DepartmentRef[],
  postalCode: string,
): DepartmentRef | undefined {
  const code = postalCodeToDepartmentCode(postalCode);
  return code ? findDepartmentByCode(departments, code) : undefined;
}

/** Code from `"Name (Code)"` or a bare department code (`75`, `2A`, `971`). */
export function parseDepartmentCodeFromText(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  const open = trimmed.lastIndexOf('(');
  const close = trimmed.lastIndexOf(')');
  const token =
    open >= 0 && close === trimmed.length - 1 && open < close
      ? trimmed.slice(open + 1, close)
      : trimmed;
  if (token === '2A' || token === '2B' || token === '2a' || token === '2b') {
    return token.toUpperCase();
  }
  if (/^\d{2,3}$/.test(token)) return token;
  return undefined;
}

/** Select value: stored label/code, then postal-code fallback. */
export function resolveStoredDepartmentCode(
  department?: string | null,
  postalCode?: string | null,
): string {
  const fromText = parseDepartmentCodeFromText(department);
  if (fromText) return fromText;
  return postalCodeToDepartmentCode(postalCode ?? '') ?? '';
}

/** BAN `depcode` first, then postal-code fallback. Label is `"Name (Code)"` when the catalogue has a row. */
export function resolveDepartmentSelection(
  departments: DepartmentRef[],
  postalCode: string,
  departmentCode?: string,
): { label: string; code: string } {
  const code = departmentCode?.trim() || postalCodeToDepartmentCode(postalCode);
  if (!code) return { label: '', code: '' };
  const departmentData = findDepartmentByCode(departments, code);
  if (!departmentData) return { label: `(${code})`, code };
  return {
    code: departmentData.code,
    label: `${departmentData.name} (${departmentData.code})`,
  };
}
